import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createSuperAdminCompany,
  getSuperAdminCompany,
  getSuperAdminPackages,
  getSuperAdminSettings,
  SuperAdminCurrencyOption,
  SuperAdminLanguageOption,
  SuperAdminPackage,
  updateSuperAdminCompany,
  uploadSuperAdminCompaniesBulk,
} from "../../services/superAdminApi";

const dateFormatOptions = ["d-m-Y", "d/m/Y", "Y-m-d", "m/d/Y"];
const timeFormatOptions = ["h:i A", "H:i", "h:i a"];

type FieldProps = {
  label: string;
  children: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-semibold text-[#402B69]">{label}</span>
    {children}
  </label>
);

const SuperAdminCompanyCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [packages, setPackages] = useState<SuperAdminPackage[]>([]);
  const [currencies, setCurrencies] = useState<SuperAdminCurrencyOption[]>([]);
  const [languages, setLanguages] = useState<SuperAdminLanguageOption[]>([]);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState<Record<string, any>>({
    company_name: "",
    company_email: "",
    company_phone: "",
    xecrm_support_id: "",
    xecrm_company_id: "",
    address: "",
    website: "",
    timezone: "Europe/London",
    date_format: "d-m-Y",
    time_format: "h:i A",
    currency_id: "",
    package_id: "",
    locale: "en",
    status: "active",
    verified: "yes",
    website_status: "active",
    subdomain: "",
    custom_domain: "",
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [settings, packagesRes, company] = await Promise.all([
          getSuperAdminSettings(),
          getSuperAdminPackages({ per_page: 100 }),
          isEditMode && id ? getSuperAdminCompany(Number(id)) : Promise.resolve(null),
        ]);
        const loadedCurrencies = settings?.currencies || [];
        const loadedLanguages = settings?.languages || [];
        const defaultGbpCurrency =
          loadedCurrencies.find((currency) => (currency.currency_code || "").toUpperCase() === "GBP") ||
          loadedCurrencies?.[0] ||
          null;
        setCurrencies(loadedCurrencies);
        setLanguages(loadedLanguages);
        setPackages(packagesRes.data || []);
        const baseForm = {
          ...form,
          currency_id: form.currency_id || defaultGbpCurrency?.id || "",
          locale: form.locale || loadedLanguages?.[0]?.language_code || "en",
          timezone: form.timezone,
          date_format: form.date_format,
          time_format: settings?.setting?.time_format || form.time_format,
        };

        if (company) {
          setForm({
            ...baseForm,
            company_name: company.company_name || "",
            company_email: company.company_email || "",
            company_phone: company.company_phone || "",
            xecrm_support_id: company.xecrm_support_id || "",
            xecrm_company_id: company.xecrm_company_id || "",
            address: company.address || "",
            website: company.website || "",
            timezone: company.timezone || baseForm.timezone,
            date_format: company.date_format || baseForm.date_format,
            time_format: company.time_format || baseForm.time_format,
            currency_id: company.currency?.id || baseForm.currency_id,
            package_id: company.package_id || "",
            locale: company.locale || baseForm.locale,
            status: company.status || "active",
            verified: company.verified || "yes",
            website_status: company.website_status || "active",
            subdomain: company.subdomain || "",
            custom_domain: company.custom_domain || "",
            name: company.owner?.name || "",
            email: company.owner?.email || "",
            password: "",
            password_confirmation: "",
          });
        } else {
          setForm(baseForm);
        }
      } catch {
        // Keep page usable with defaults.
      }
    };
    loadMeta();
  }, [id, isEditMode]);

  const canCreate = useMemo(() => {
    return (
      form.company_name &&
      form.company_email &&
      form.company_phone &&
      form.address &&
      form.currency_id &&
      form.timezone &&
      form.date_format &&
      form.time_format &&
      form.locale &&
      (isEditMode || (form.package_id && form.name && form.email && form.password && form.password_confirmation))
    );
  }, [form, isEditMode]);

  const handleSubmit = async () => {
    if (!canCreate) return;
    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      const payload = new FormData();
      payload.append("company_name", form.company_name);
      payload.append("company_email", form.company_email);
      payload.append("company_phone", form.company_phone);
      payload.append("xecrm_support_id", form.xecrm_support_id || "");
      payload.append("xecrm_company_id", form.xecrm_company_id || "");
      payload.append("address", form.address);
      payload.append("website", form.website || "");
      payload.append("timezone", form.timezone);
      payload.append("date_format", form.date_format);
      payload.append("time_format", form.time_format);
      payload.append("locale", form.locale);
      payload.append("currency_id", String(form.currency_id));
      payload.append("status", form.status || "active");
      payload.append("verified", form.verified || "yes");
      payload.append("website_status", form.website_status || "active");
      if (form.subdomain?.trim()) {
        payload.append("subdomain", form.subdomain.trim().toLowerCase());
      }
      if (form.custom_domain?.trim()) {
        payload.append("custom_domain", form.custom_domain.trim());
      }
      if (logoFile) {
        payload.append("logo", logoFile);
      }

      if (!isEditMode) {
        if (!form.package_id) {
          setError("Please select a package.");
          setSubmitting(false);
          return;
        }
        payload.append("package_id", String(form.package_id));
        payload.append("name", form.name);
        payload.append("email", form.email);
        payload.append("password", form.password);
        payload.append("password_confirmation", form.password_confirmation);
        await createSuperAdminCompany(payload as any);
      } else {
        if (form.package_id) payload.append("package_id", String(form.package_id));
        await updateSuperAdminCompany(Number(id), payload as any);
      }

      const successMessage = isEditMode ? "Company updated successfully." : "Company created successfully.";
      setSuccess(successMessage);
      toast.success(successMessage);
      setTimeout(() => navigate("/superadmin/companies"), 700);
    } catch (e: any) {
      setError(e?.response?.data?.message || (isEditMode ? "Failed to update company." : "Failed to create company."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    try {
      setBulkSubmitting(true);
      setError("");
      setSuccess("");
      await uploadSuperAdminCompaniesBulk(bulkFile);
      const successMessage = "Businesses uploaded successfully.";
      setSuccess(successMessage);
      toast.success(successMessage);
      setBulkFile(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to upload bulk businesses.");
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 p-1">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">{isEditMode ? "Edit Business" : "Add Business"}</h1>
          <p className="text-sm text-gray-500">
            Web version aligned {isEditMode ? "edit" : "create"} company page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/companies")}
          className="rounded-lg border border-[#E5DDF5] bg-white px-4 py-2 text-sm font-semibold text-[#402B69] hover:bg-[#F7F3FF]"
        >
          Back to Companies
        </button>
      </div>

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {success ? <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div> : null}

      {!isEditMode ? (
        <div className="rounded-xl border border-[#E5DDF5] bg-white shadow-sm">
          <div className="border-b bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">Add Bulk Businesses</div>
          <div className="p-4 space-y-3">
            <label className="block text-sm font-semibold text-[#402B69]">Upload CSV *</label>
            <input
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            <button
              type="button"
              onClick={handleBulkUpload}
              disabled={bulkSubmitting || !bulkFile}
              className="rounded-lg bg-[#885ABB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
            >
              {bulkSubmitting ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-[#E5DDF5] bg-white shadow-sm">
        <div className="border-b bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">Add Business</div>
        <div className="p-4 space-y-5">
          <h3 className="text-sm font-semibold text-[#402B69]">Company Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Business Name *">
              <input value={form.company_name} onChange={(e) => setForm((p: any) => ({ ...p, company_name: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter business name" />
            </Field>
            <Field label="Business Email *">
              <input value={form.company_email} onChange={(e) => setForm((p: any) => ({ ...p, company_email: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter business email" />
            </Field>
            <Field label="Business Phone *">
              <input value={form.company_phone} onChange={(e) => setForm((p: any) => ({ ...p, company_phone: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter business phone" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-[#402B69]">Logo (suggested 400x110)</p>
              <label className="flex h-[120px] cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-[#CDB5EE] bg-[#F6F0FF] text-[#885ABB]">
                {logoFile ? logoFile.name : "Drag and drop"}
                <input type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <Field label="Address *">
              <textarea value={form.address} onChange={(e) => setForm((p: any) => ({ ...p, address: e.target.value }))} className="min-h-[120px] w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter address" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Business Website">
              <input value={form.website} onChange={(e) => setForm((p: any) => ({ ...p, website: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter website URL" />
            </Field>
            <Field label="Timezone *">
              <input value={form.timezone} onChange={(e) => setForm((p: any) => ({ ...p, timezone: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter timezone" />
            </Field>
            <Field label="Currency *">
              <select value={form.currency_id} onChange={(e) => setForm((p: any) => ({ ...p, currency_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                <option value="">Select currency</option>
                {currencies.map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.currency_symbol} ({currency.currency_code})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Language *">
              <select value={form.locale} onChange={(e) => setForm((p: any) => ({ ...p, locale: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                <option value="">Select language</option>
                {languages.map((language) => (
                  <option key={language.id} value={language.language_code || ""}>
                    {language.language_name || language.language_code}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Date Format *">
              <select value={form.date_format} onChange={(e) => setForm((p: any) => ({ ...p, date_format: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                {dateFormatOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>
            <Field label="Time Format *">
              <select value={form.time_format} onChange={(e) => setForm((p: any) => ({ ...p, time_format: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                {timeFormatOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </Field>
            <Field label="Package *">
              <select required value={form.package_id} onChange={(e) => setForm((p: any) => ({ ...p, package_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                <option value="">Select package</option>
                {packages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.name}</option>)}
              </select>
            </Field>
            <Field label="Subdomain (optional)">
              <input value={form.subdomain} onChange={(e) => setForm((p: any) => ({ ...p, subdomain: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter subdomain" />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Custom Domain (optional)">
              <input value={form.custom_domain} onChange={(e) => setForm((p: any) => ({ ...p, custom_domain: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter custom domain" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm((p: any) => ({ ...p, status: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="deactive">Deactive</option>
                </select>
              </Field>
              <Field label="Website Status">
                <select value={form.website_status} onChange={(e) => setForm((p: any) => ({ ...p, website_status: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                  <option value="active">Website Active</option>
                  <option value="inactive">Website Inactive</option>
                </select>
              </Field>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="XECRM Support ID">
                <input value={form.xecrm_support_id} onChange={(e) => setForm((p: any) => ({ ...p, xecrm_support_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter XECRM support id" />
              </Field>
              <Field label="XECRM Company ID">
                <input value={form.xecrm_company_id} onChange={(e) => setForm((p: any) => ({ ...p, xecrm_company_id: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter XECRM company id" />
              </Field>
              <Field label="Verified">
                <select value={form.verified} onChange={(e) => setForm((p: any) => ({ ...p, verified: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]">
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
            </div>
          </div>

          {!isEditMode ? (
            <>
              <hr className="border-[#E5DDF5]" />
              <h3 className="text-sm font-semibold text-[#402B69]">Owner / Admin Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name *">
                  <input value={form.name} onChange={(e) => setForm((p: any) => ({ ...p, name: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter owner name" />
                </Field>
                <Field label="Email *">
                  <input value={form.email} onChange={(e) => setForm((p: any) => ({ ...p, email: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter owner email" />
                </Field>
                <Field label="Password * (min 6)">
                  <input type="password" value={form.password} onChange={(e) => setForm((p: any) => ({ ...p, password: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Enter password" />
                </Field>
                <Field label="Confirm Password *">
                  <input type="password" value={form.password_confirmation} onChange={(e) => setForm((p: any) => ({ ...p, password_confirmation: e.target.value }))} className="w-full rounded-lg border border-[#D5C6EE] px-3 py-2 text-sm focus:border-[#885ABB] focus:outline-none focus:ring-2 focus:ring-[#E9DFFF]" placeholder="Confirm password" />
                </Field>
              </div>
            </>
          ) : null}

          <div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canCreate || submitting}
              className="rounded-lg bg-[#885ABB] px-5 py-2 text-sm font-semibold text-white hover:bg-[#7748A4] disabled:opacity-50"
            >
              {submitting ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminCompanyCreate;
